// @greenlight-spec: 11-observability.md#per-app-metrics-persistence
// @greenlight-spec: 34-workloads.md
import { z } from 'zod';
/**
 * What one app reserves against the app nodes and what it is measured using.
 * CPU is millicores (`_m`), memory MiB (`_mb`, matching the `app_metrics`
 * snapshot's own unit). Either side is `null` when its source had nothing to
 * report — a never-deployed app, a metrics-server miss, or a sample written
 * before the reserved fields existed. Nulls stay null: an unmeasured app is not
 * an idle one, and ranking it as such would point IT at the wrong app.
 */
export const AppReservationSchema = z.object({
    app_id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    collected_at: z.string().nullable(),
    cpu_reserved_m: z.number().nullable(),
    cpu_used_m: z.number().nullable(),
    memory_reserved_mb: z.number().nullable(),
    memory_used_mb: z.number().nullable(),
});
export const AppReservationsSchema = z.object({
    /** The largest reserved-but-unused apps, biggest first, bounded to the top ten. */
    items: z.array(AppReservationSchema),
    /**
     * How many apps the ranking covered, so a reader can tell the returned ten
     * from the fleet behind them rather than reading a bounded list as the whole
     * picture.
     */
    ranked_count: z.number(),
    /**
     * Apps left out of the ranking because neither resource could be compared.
     * Reported as a count so the page can say what it could not rank instead of
     * implying the ranking covers the fleet.
     */
    unmeasured_count: z.number(),
});
/** Reserved minus used, floored at zero. `null` when either side is unknown. */
export function reservationGap(reserved, used) {
    if (reserved === null || used === null)
        return null;
    return Math.max(0, reserved - used);
}
/**
 * Percent of a reservation the app actually spends, to one decimal. `null` when
 * either side is unknown; `null` for a zero reservation too, since nothing was
 * reserved for the usage to be a share of.
 */
export function reservationUtilizationPercent(reserved, used) {
    if (reserved === null || used === null || reserved <= 0)
        return null;
    return Math.round((used / reserved) * 1000) / 10;
}
/**
 * Reserved-but-unused CPU and memory for one app. This is what the fleet
 * ranking sorts on, and it is absolute rather than proportional on purpose: a
 * 25m app spending nothing wastes nothing worth finding, while the half-core an
 * idle app holds is half a core no other app can schedule.
 */
export function unusedReservation(row) {
    return {
        cpu_m: reservationGap(row.cpu_reserved_m, row.cpu_used_m),
        memory_mb: reservationGap(row.memory_reserved_mb, row.memory_used_mb),
    };
}
/**
 * Ranks the fleet by unused CPU first, unused memory second. CPU leads because
 * it is the resource requests saturate first
 * (11-observability.md#app-runtime-capacity). An app with neither figure sorts
 * last rather than as zero waste — nothing is known about it either way.
 */
export function compareByUnusedReservation(a, b) {
    const ua = unusedReservation(a);
    const ub = unusedReservation(b);
    const rank = (v) => v.cpu_m === null && v.memory_mb === null
        ? [1, 0, 0]
        : [0, -(v.cpu_m ?? 0), -(v.memory_mb ?? 0)];
    const [ka, ca, ma] = rank(ua);
    const [kb, cb, mb] = rank(ub);
    return ka - kb || ca - cb || ma - mb || a.slug.localeCompare(b.slug);
}
//# sourceMappingURL=app-reservation.js.map